import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import axios from 'axios';
import {
  Buildings, GameController, BookOpen, GraduationCap, Megaphone,
  Globe, Envelope, ShieldCheck, Package, ArrowLeft
} from '@phosphor-icons/react';
import BottomNav from '../components/BottomNav';

const API_URL = process.env.REACT_APP_BACKEND_URL;

export default function PublicPublisherPage() {
  const { publisherSlug } = useParams();
  const navigate = useNavigate();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [tab, setTab] = useState('games');

  useEffect(() => {
    axios.get(`${API_URL}/api/publisher-portal/public/${publisherSlug}`)
      .then(r => setData(r.data))
      .catch(() => {})
      .finally(() => setLoading(false));
  }, [publisherSlug]);

  if (loading) {
    return (
      <div className="app-container min-h-screen bg-[#06060A] flex items-center justify-center">
        <div className="w-12 h-12 border-4 border-[#F59E0B] border-t-transparent rounded-full animate-spin" />
      </div>
    );
  }

  if (!data) {
    return (
      <div className="app-container min-h-screen bg-[#06060A] flex items-center justify-center">
        <div className="text-center">
          <Buildings size={48} className="mx-auto text-[#333] mb-4" />
          <p className="text-white font-medium text-lg">Publisher not found</p>
          <button onClick={() => navigate('/publishers')} className="text-[#F59E0B] text-sm mt-2 hover:underline">Browse publishers</button>
        </div>
      </div>
    );
  }

  const TABS = [
    { id: 'games', label: 'Games', count: data.games?.length || 0 },
    { id: 'tutorials', label: 'Tutorials', count: data.tutorials?.length || 0 },
    { id: 'rules', label: 'Rules', count: data.rulesets?.length || 0 },
    { id: 'campaigns', label: 'Campaigns', count: data.campaigns?.length || 0 },
  ];

  return (
    <div className="app-container min-h-screen bg-[#06060A] pb-24" data-testid="public-publisher-page">
      {/* Header */}
      <div className="relative">
        {data.cover_image_url ? (
          <div className="h-40 bg-cover bg-center" style={{ backgroundImage: `url(${data.cover_image_url})` }}>
            <div className="absolute inset-0 bg-gradient-to-b from-transparent to-[#06060A]" />
          </div>
        ) : (
          <div className="h-40 bg-gradient-to-br from-[#F59E0B]/20 to-[#9D4CDD]/20" />
        )}
        <button onClick={() => navigate(-1)}
          className="absolute top-4 left-4 w-8 h-8 rounded-full bg-black/50 backdrop-blur-sm flex items-center justify-center text-white">
          <ArrowLeft size={18} />
        </button>
      </div>

      <div className="px-5 -mt-10 relative z-10">
        {/* Publisher Info */}
        <div className="flex items-end gap-4 mb-5">
          <div className="w-20 h-20 rounded-2xl bg-[#F59E0B]/10 border-4 border-[#06060A] flex items-center justify-center">
            {data.logo_url ? (
              <img src={data.logo_url} alt={data.name} className="w-full h-full rounded-xl object-cover" />
            ) : (
              <Buildings size={36} weight="duotone" className="text-[#F59E0B]" />
            )}
          </div>
          <div className="flex-1 pb-1">
            <div className="flex items-center gap-2">
              <h1 className="text-xl font-bold text-white" style={{ fontFamily: 'Unbounded' }}>{data.name}</h1>
              {data.verified && <ShieldCheck size={18} weight="fill" className="text-[#10B981]" />}
            </div>
            <div className="flex items-center gap-3 mt-1 text-xs text-[#666]">
              {data.website_url && (
                <a href={data.website_url} target="_blank" rel="noreferrer"
                  className="flex items-center gap-1 text-[#F59E0B] hover:underline">
                  <Globe size={12} /> Website
                </a>
              )}
              {data.support_email && (
                <span className="flex items-center gap-1"><Envelope size={12} /> {data.support_email}</span>
              )}
            </div>
          </div>
        </div>

        {data.description && (
          <p className="text-sm text-[#888] mb-6 leading-relaxed">{data.description}</p>
        )}

        {/* Tabs */}
        <div className="flex gap-1 mb-5 overflow-x-auto" style={{ scrollbarWidth: 'none' }}>
          {TABS.map(t => (
            <button key={t.id} onClick={() => setTab(t.id)}
              className={`px-4 py-2 rounded-lg text-sm font-medium whitespace-nowrap transition-colors ${
                tab === t.id ? 'bg-[#F59E0B]/10 text-[#F59E0B]' : 'text-[#666] hover:text-white'
              }`} data-testid={`public-tab-${t.id}`}>
              {t.label} <span className="text-[10px] ml-1 text-[#555]">{t.count}</span>
            </button>
          ))}
        </div>

        {/* Content */}
        {tab === 'games' && (
          <div className="space-y-3">
            {data.games?.length === 0 && <p className="text-[#555] text-sm">No games listed.</p>}
            {data.games?.map((g, i) => (
              <div key={g.slug || i} className="bg-white/[0.02] border border-white/[0.06] rounded-xl p-4">
                <div className="flex items-center gap-3">
                  <GameController size={28} weight="duotone" className="text-[#F59E0B] flex-shrink-0" />
                  <div>
                    <p className="text-white font-semibold">{g.name}</p>
                    <p className="text-[11px] text-[#555]">{g.min_players}-{g.max_players} players · {g.play_time || '?'}</p>
                    {g.description && <p className="text-xs text-[#777] mt-1 line-clamp-2">{g.description}</p>}
                  </div>
                </div>
              </div>
            ))}
          </div>
        )}

        {tab === 'tutorials' && (
          <div className="space-y-3">
            {data.tutorials?.length === 0 && <p className="text-[#555] text-sm">No published tutorials.</p>}
            {data.tutorials?.map(t => (
              <div key={t.id} className="bg-white/[0.02] border border-white/[0.06] rounded-xl p-4">
                <div className="flex items-center gap-3">
                  <GraduationCap size={24} weight="duotone" className="text-[#10B981] flex-shrink-0" />
                  <div>
                    <p className="text-white font-medium">{t.title}</p>
                    <div className="text-[11px] text-[#555] flex gap-2 mt-0.5">
                      {t.game_name && <span>{t.game_name}</span>}
                      <span className="capitalize">{t.difficulty}</span>
                      <span className="capitalize">{(t.tutorial_type || '').replace(/_/g, ' ')}</span>
                      {t.steps?.length > 0 && <span>{t.steps.length} steps</span>}
                    </div>
                  </div>
                </div>
              </div>
            ))}
          </div>
        )}

        {tab === 'rules' && (
          <div className="space-y-3">
            {data.rulesets?.length === 0 && <p className="text-[#555] text-sm">No published rulesets.</p>}
            {data.rulesets?.map(r => (
              <div key={r.id} className="bg-white/[0.02] border border-white/[0.06] rounded-xl p-4">
                <div className="flex items-center gap-3">
                  <BookOpen size={24} weight="duotone" className="text-[#00F0FF] flex-shrink-0" />
                  <div>
                    <p className="text-white font-medium">{r.title}</p>
                    <div className="text-[11px] text-[#555]">
                      v{r.version} {r.is_current && <span className="text-[#00F0FF]">· Current</span>}
                    </div>
                    {r.summary && <p className="text-xs text-[#777] mt-1">{r.summary}</p>}
                  </div>
                </div>
              </div>
            ))}
          </div>
        )}

        {tab === 'campaigns' && (
          <div className="space-y-3">
            {data.campaigns?.length === 0 && <p className="text-[#555] text-sm">No active campaigns.</p>}
            {data.campaigns?.map(c => (
              <div key={c.id} className="bg-white/[0.02] border border-white/[0.06] rounded-xl p-4">
                <div className="flex items-center gap-3">
                  <Megaphone size={24} weight="duotone" className="text-[#F59E0B] flex-shrink-0" />
                  <div>
                    <p className="text-white font-medium">{c.title}</p>
                    <p className="text-xs text-[#777] mt-0.5">{c.description}</p>
                  </div>
                </div>
              </div>
            ))}
          </div>
        )}

        {/* Editions & Expansions */}
        {(data.editions?.length > 0 || data.expansions?.length > 0) && (
          <div className="mt-8">
            <h3 className="text-sm font-semibold text-white mb-3 flex items-center gap-2">
              <Package size={16} className="text-[#9D4CDD]" /> Editions & Expansions
            </h3>
            <div className="grid grid-cols-2 gap-2">
              {data.editions?.map(e => (
                <div key={e.id} className="bg-white/[0.02] border border-white/[0.06] rounded-lg p-3">
                  <p className="text-white text-sm font-medium">{e.name}</p>
                  <p className="text-[10px] text-[#555]">Edition {e.year && `· ${e.year}`}</p>
                </div>
              ))}
              {data.expansions?.map(e => (
                <div key={e.id} className="bg-white/[0.02] border border-white/[0.06] rounded-lg p-3">
                  <p className="text-white text-sm font-medium">{e.name}</p>
                  <p className="text-[10px] text-[#555]">Expansion {e.standalone && '· Standalone'}</p>
                </div>
              ))}
            </div>
          </div>
        )}
      </div>

      <BottomNav />
    </div>
  );
}
